import dayjs from 'dayjs';
import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { FC } from 'react';
import { Tooltip } from 'react-tooltip';
import AddIcon from '@/assets/add.svg';
import EditIcon from '@/assets/edit.svg';
import ViewIcon from '@/assets/view.svg';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerProps } from '@/components/ui/drawer';
import { HeadingGroup } from '@/components/ui/heading-group';
import { Modal } from '@/components/ui/modal';
import { strings } from '@/constants/strings';
import { useCurrentOrganisation } from '@/hooks/use-current-organisation';
import { useGroups } from '@/hooks/use-groups';
import { useOpenState } from '@/hooks/use-open-state';
import { useOrganisations } from '@/hooks/use-organisations';
import { useRoles } from '@/hooks/use-roles';
import { useUser } from '@/hooks/use-user';
import { useUsers } from '@/hooks/use-users';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { api } from '@/redux/api';
import { composeTableOptions } from '@/table';
import { ApplicationUser } from '@/types/application-user';
import { findById } from '@/utils/find-by-id';
import { canActiveUserAssignAnyOrganisation } from '@/utils/users/permissions/can-active-user-assign-any-organisation';
import { canActiveUserEditUser } from '@/utils/users/permissions/can-active-user-edit-user';
import { CreateUserForm } from './drawer-views/create-user-form';
import { EditUserForm } from './drawer-views/edit-user-form';
import { ViewUser } from './drawer-views/view-user';

const columns: MRT_ColumnDef<ApplicationUser>[] = [
  {
    id: 'name',
    accessorFn: ({ firstName, lastName }) => `${firstName} ${lastName}`,
    header: 'Name',
    size: 120,
    Cell: ({ renderedCellValue }) => renderedCellValue,
  },
  {
    accessorKey: 'email',
    header: 'Email',
    size: 150,
  },
  {
    accessorKey: 'role.roleName',
    filterVariant: 'multi-select',
    header: 'Role',
    size: 150,
  },
  {
    id: 'organisation',
    accessorFn: ({ organisation }) => organisation?.name,
    header: 'Organisation',
    size: 120,
  },
  {
    id: 'permittedGroups',
    header: 'Allowed Groups',
    accessorFn: ({ permittedGroups }) => {
      if (permittedGroups) {
        return permittedGroups.map((group, index, array) => {
          if (index + 1 < array.length) {
            return `${group.groupName}, `;
          } else {
            return group.groupName;
          }
        });
      }
    },
  },
  {
    id: 'lastLogin',
    header: 'Last Login',
    accessorFn: ({ lastLogin }) =>
      lastLogin ? dayjs(lastLogin).format('DD/MM/YYYY HH:mm') : '-',
    size: 150,
  },
];

type OpenContext = { userId: number };

type DrawerView = 'view' | 'edit' | 'create';

type DrawerContext = { view: DrawerView } & Partial<OpenContext>;

export const UsersPage: FC = () => {
  const { user: activeUser } = useUser();
  const { data: roles } = useRoles();
  const { data: organisations } = useOrganisations();
  const { isUsingOrganisationOverride, organisation: currentOrganisation } =
    useCurrentOrganisation();
  const { data: users, isLoading: isLoadingUsers } = useUsers();
  const { data: groups } = useGroups();
  const {
    state: drawerState,
    open: openDrawer,
    toggleOpen: toggleDrawerOpen,
  } = useOpenState<DrawerContext>({ view: 'create' });
  const {
    state: deleteConfirmationState,
    open: openDeleteConfirmation,
    toggleOpen: toggleDeleteConfirmationOpen,
  } = useOpenState<OpenContext>({ userId: -1 });
  const [createUser] = api.useCreateUserMutation();
  const [updateUser] = api.useUpdateUserMutation();
  const [deleteUser] = api.useDeleteUserMutation();

  const canAssignAnyOrganisation = canActiveUserAssignAnyOrganisation(
    activeUser,
    isUsingOrganisationOverride,
  );

  const table = useMaterialReactTable({
    ...composeTableOptions<ApplicationUser>({ enableRowActions: true }),
    columns,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableStickyHeader: true,
    data: users,
    state: {
      isLoading: isLoadingUsers,
      showColumnFilters: true,
      ...(isLoadingUsers
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    renderRowActionMenuItems: ({ row, table, closeMenu }) => [
      <MRT_ActionMenuItem
        icon={<ViewIcon />}
        key="view"
        label="View"
        onClick={() => {
          closeMenu();
          openDrawer({ view: 'view', userId: row.original.id });
        }}
        table={table}
      />,
      canActiveUserEditUser(activeUser, row.original) ? (
        <MRT_ActionMenuItem
          icon={<EditIcon />}
          key="edit"
          label="Edit"
          onClick={() => {
            closeMenu();
            openDrawer({ view: 'edit', userId: row.original.id });
          }}
          table={table}
        />
      ) : null,
    ],
  });

  const getDrawerTitleProps = (): Pick<DrawerProps, 'title' | 'subtitle'> => {
    switch (drawerState.context.view) {
      case 'create':
        return {
          title: strings.users.drawer_create_form.title,
        };
      case 'edit':
        return {
          title: strings.users.drawer_edit_form.title,
        };
      case 'view':
        return {
          title: strings.users.drawer_view.title,
        };
      default:
        return {
          title: '',
        };
    }
  };

  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'create':
        return (
          <CreateUserForm
            activeUser={activeUser}
            canAssignAnyOrganisation={canAssignAnyOrganisation}
            currentOrganisation={currentOrganisation}
            groups={groups}
            onCancel={() => toggleDrawerOpen(false)}
            onSubmit={(data) => {
              const roleId = data.roleId;
              const organisationId = canAssignAnyOrganisation
                ? typeof data.organisationId === 'number'
                  ? data.organisationId
                  : undefined
                : currentOrganisation?.id;
              const permittedGroups = [];
              for (const item of data.permittedGroups ?? []) {
                if (typeof item == 'object') {
                  permittedGroups.push(item.value);
                }
              }

              createUser({
                ...data,
                roleId,
                organisationId,
                permittedGroups,
              });
              toggleDrawerOpen(false);
            }}
            organisations={organisations}
            permittedGroupValues={[]}
            roles={roles}
          />
        );
      case 'edit': {
        const user = findById(users, drawerState.context.userId ?? -1);
        if (user) {
          return (
            <EditUserForm
              activeUser={activeUser}
              canAssignAnyOrganisation={canAssignAnyOrganisation}
              currentOrganisation={currentOrganisation}
              groups={groups}
              onCancel={() => toggleDrawerOpen(false)}
              onDelete={() => openDeleteConfirmation({ userId: user.id })}
              onSubmit={(data) => {
                const roleId = data.roleId ?? user.role.id;
                const organisationId =
                  roleId === 1
                    ? undefined
                    : canAssignAnyOrganisation
                      ? typeof data.organisationId === 'number'
                        ? data.organisationId
                        : undefined
                      : currentOrganisation?.id;

                const permittedGroups = [];
                for (const item of data.permittedGroups ?? []) {
                  if (typeof item == 'object') {
                    permittedGroups.push(item.value);
                  }
                }

                updateUser({
                  ...data,
                  id: user.id,
                  roleId,
                  organisationId,
                  permittedGroups,
                });
                toggleDrawerOpen(false);
              }}
              organisations={organisations}
              permittedGroupValues={user.permittedGroups ?? []}
              roles={roles}
              user={user}
            />
          );
        }
        break;
      }
      case 'view': {
        const user = findById(users, drawerState.context.userId ?? -1);
        if (user) {
          return (
            <ViewUser
              canEdit={canActiveUserEditUser(activeUser, user)}
              onClose={() => toggleDrawerOpen(false)}
              onEdit={() => {
                openDrawer({
                  view: 'edit',
                  userId: drawerState.context.userId,
                });
              }}
              user={user}
            />
          );
        }
        break;
      }
      default:
        break;
    }

    if (drawerState.open) {
      toggleDrawerOpen(false);
    }
    return null;
  };

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePostfix={strings.users.subtitlePostfix}
          subtitlePrefix={strings.users.subtitlePrefix}
          subtitleTooltip={strings.users.subtitleTooltip}
          title={strings.users.title}
          tooltip={strings.users.tooltip}
        />
        <Tooltip anchorSelect="#addUserTooltip">
          Add a user to your organisation using their email
        </Tooltip>
        <a id="addUserTooltip">
          <Button onClick={() => openDrawer({ view: 'create' })}>
            <AddIcon /> Add User
          </Button>
        </a>
      </HeadingGroupContainerLayout>
      <MaterialReactTable table={table} />
      <Drawer
        {...getDrawerTitleProps()}
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
      >
        {renderDrawerContent()}
      </Drawer>
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleDeleteConfirmationOpen(false)}
            variant="secondary"
          >
            {strings.users.modal_confirm_delete.cancel}
          </Button>,
          <Button
            key="delete"
            onClick={() => {
              deleteUser({ id: deleteConfirmationState.context.userId });
              toggleDeleteConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            {strings.users.modal_confirm_delete.delete}
          </Button>,
        ]}
        description={strings.users.modal_confirm_delete.description}
        open={deleteConfirmationState.open}
        setOpen={toggleDeleteConfirmationOpen}
        title={strings.users.modal_confirm_delete.title}
      />
    </>
  );
};
